import { useState } from "react";
import PropTypes from "prop-types";
import capitalize from "capitalize";
import Modal from "@shared/v2/Modal";
import { UserSolidV6 } from "@shared/v2/Icomoon";
import BaseEvent from "./BaseEvent";
import { isServiceUpdater } from "./helpers/updaterAttribution";

const SHORT_STATUS = {
  new: "New",
  unqualified: "Unqual",
  watch: "Watch",
  nurture: "Nurt",
  hot: "Hot",
  "past-client": "Past",
  inactive: "Inactiv",
  archived: "Arch",
  trash: "Trash",
};

const ALLOWED_FIELDS = [
  "personal_email",
  "work_email",
  "other_email",
  "mobile_phone",
  "home_phone",
  "work_phone",
  "other_phone",
  "source",
  "status",
];

const AddedBy = ({ timelineFields }) => {
  const updaterName = timelineFields.updater_name;
  if (updaterName === "API") return null;
  if (updaterName === "Import") return "Added via import";
  if (updaterName === "Tracking Number") {
    return (
      <>
        Lead automatically created from Tracking Number{" "}
        <span className="tw-font-bold">&quot;{timelineFields.tracking_number_name}&quot;</span>`
      </>
    );
  }
  // Must precede the fallback below, which assumes updaterName is a person's name.
  if (isServiceUpdater(timelineFields)) return `Automatically created by ${updaterName}`;
  if (updaterName) return `Manually added by ${updaterName}`;
  return null;
};

AddedBy.propTypes = {
  timelineFields: PropTypes.shape().isRequired,
};

const statusDetail = (event, change) => {
  const prevStatus = change[0];
  const newStatus = change[1];

  const prevShortStatus = SHORT_STATUS[prevStatus];
  const newShortStatus = SHORT_STATUS[newStatus];

  return (
    <div key={`${event.id}-status-${change}`} className="tw-flex tw-items-center tw-gap-[4px]">
      {"Status: "}
      {prevStatus && (
        <>
          <span className={`btn btn-sm person-status-${prevStatus}`}>{prevShortStatus}</span>
          <span>to</span>
        </>
      )}
      <span className={`btn btn-sm person-status-${newStatus}`}>{newShortStatus}</span>
    </div>
  );
};

const standardDetail = (event, titleizedAttribute, change) => (
  <div key={`${event.id}-${titleizedAttribute}-${change}`}>
    {titleizedAttribute}:{" "}
    {change[0] && <span className="timeline-person-original-field">{`(was ${change[0]}) `}</span>}
    {!change[1] ? <span className="timeline-person-removed-field">removed</span> : change[1]}
  </div>
);

const Details = ({ event, event: { timeline_fields: timelineFields } }) => (
  <div className="tw-flex tw-flex-col tw-gap-[8px]">
    {ALLOWED_FIELDS.map((field) => {
      let change = timelineFields[field];

      if (field === "source" && !change) {
        change = ["", "Unknown"];
      }

      if (change) {
        const titleizedAttribute = field
          .split("_")
          .map((element) => capitalize(element))
          .join(" ");

        if (titleizedAttribute === "Status") {
          return statusDetail(event, change);
        }
        return standardDetail(event, titleizedAttribute, change);
      }
      return null;
    })}
  </div>
);

Details.propTypes = {
  event: PropTypes.shape().isRequired,
};

const TimelineLeadCreateEvent = ({ event, event: { timeline_fields: timelineFields } }) => {
  const [showModal, setShowModal] = useState(false);

  const contactName =
    `${timelineFields.first_name?.[1] || ""} ${timelineFields.last_name?.[1] || ""}`.trim() || "No Name";

  return (
    <BaseEvent
      title={
        <div>
          <span className="tw-font-semibold">{contactName} </span>became a contact
        </div>
      }
      icon={<UserSolidV6 />}
      interactedAt={event.interacted_at_absolute}
      onClick={() => setShowModal(true)}
    >
      <Details event={event} />
      <AddedBy timelineFields={timelineFields} />
      <Modal
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentClassName="tw-max-w-[480px] tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col tw-gap-[32px]"
        show={showModal}
        onHide={() => setShowModal(false)}
      >
        <Modal.Header title="Profile Created" closeButton />
        <Modal.Body>
          <div className="tw-font-semibold">Details:</div>
          <Details event={event} />
          <AddedBy timelineFields={timelineFields} />
        </Modal.Body>
      </Modal>
    </BaseEvent>
  );
};

TimelineLeadCreateEvent.propTypes = {
  event: PropTypes.shape().isRequired,
};

export default TimelineLeadCreateEvent;
